import httpClient from "@/services";
import { API_ENDPOINTS } from "@/services/endpoints";

import { parseMoney, toHcbbProcedureId } from "@/lib/hcbb-cost-estimator-utils";

import type { HcbbCostSearch } from "@/lib/hcbb-cost-estimator-utils";
import type { IUserInfo } from "@/hooks/use-user-info";
import type Place from "@/model/place";
import type {
  HcbbEstimateRequest,
  HcbbFacilityProvider,
  HcbbFacilityProvidersRequest,
  HcbbFacilityProvidersResponse,
  HcbbFacilityResult,
  HcbbFreeTextSearchResponse,
  HcbbPriceHighlight,
  HcbbPricingSection,
  HcbbProcedureDetailsResponse,
  HcbbProfessionalRef,
  HcbbProfessionalResult,
  HcbbProfessionalsRequest,
  HcbbRecentSearch,
  HcbbRecentSearchesRequest,
  HcbbRequestBody,
  HcbbSearchRequest,
  HcbbSearchResultRow,
} from "@/model/hcbb-cost-estimator";

const toArray = <T>(value: unknown): T[] =>
  Array.isArray(value) ? (value as T[]) : [];

/**
 * The member context every HCBB request needs, beyond what `useUserInfo`
 * gives every other service: the medical coverage period HCBB prices
 * against. That period lives in the coverage-years query cache, not in
 * `IUserInfo`, so callers build this by hand — see `useHcbbMemberInfo`.
 */
export type HcbbMemberInfo = IUserInfo & {
  coverageStartDate: string;
  coverageEndDate: string;
};

/**
 * The body both endpoints answer with, unwrapped.
 *
 * The sibling search endpoints wrap their payload in `results`, so the content
 * is taken from there when it is present and from the body itself when it is
 * not.
 */
const toPayload = (body: unknown): Record<string, unknown> => {
  const envelope = (body ?? {}) as Record<string, unknown>;
  return (envelope.results ?? envelope) as Record<string, unknown>;
};

/** Every request carries the same member, plan and coverage identifiers. */
const toMemberPayload = (userInfo: HcbbMemberInfo) => ({
  groupId: userInfo.groupId,
  memberId: userInfo.memberId,
  divisionId: userInfo.divisionId,
  planId: userInfo.planId,
  employeeId: userInfo.employeeId,
  coverageStartDate: userInfo.coverageStartDate,
  coverageEndDate: userInfo.coverageEndDate,
});

/**
 * The searched location's zip, when it has one.
 *
 * `Place` doesn't always carry a postal code, so callers fall back to the
 * member's own `userInfo.zipCode` — that fallback lives at each call site,
 * not here, since only the caller knows which member to fall back to.
 */
const toZipCode = (
  targetLocation: Place | null | undefined,
): string | undefined => targetLocation?.postal_code || undefined;

/**
 * The searched location's display text, exactly as the typeahead showed it.
 *
 * Sent alongside the zip so the estimate carries what the member actually
 * picked (or the reverse-geocoded "current location" text), not only the
 * postal code derived from it. Unlike the zip there is no member-level
 * fallback for this — an unpicked location simply sends an empty address.
 */
const toAddress = (targetLocation: Place | null | undefined): string =>
  targetLocation?.description ?? "";

/**
 * A facility's NPIs, normalised to the array of strings the facility-providers
 * endpoint takes.
 *
 * The estimate has sent this as a bare comma-separated string as often as an
 * array, so both are read here rather than trusting the wire to commit to
 * one; either way an NPI is a string of digits, and an empty one is dropped
 * rather than sent as a blank entry.
 */
const toNpisArray = (value: unknown): string[] => {
  const raw = Array.isArray(value) ? value : String(value ?? "").split(",");

  return raw.map((npi) => String(npi).trim()).filter(Boolean);
};

/** The one section the physician's own fee is filed under. */
const PHYSICIAN_FEE_TYPE = "physician fee";

/**
 * Every section of the highlight's price breakdown besides the physician's
 * own fee, summed.
 *
 * Section types have been seen in mixed case, so the physician fee is matched
 * lower-cased rather than missed — a miss here would count it twice over,
 * once on its own and once again in this total.
 */
const toAdditionalFees = (sections: HcbbPricingSection[]): number =>
  sections.reduce((total, section) => {
    if (section.type?.toLowerCase() === PHYSICIAN_FEE_TYPE) return total;

    return total + (parseMoney(section.fee) ?? 0);
  }, 0);

/**
 * The response body, or an error if that is what it is really carrying.
 *
 * These endpoints can answer 200 with an `error` field instead of a status
 * code. Left alone that reads as a successful empty payload — "no providers
 * near you" rather than "this failed" — so it is raised here, once, for every
 * request this file makes.
 */
const raiseOnErrorBody = (response: {
  data?: { error?: string; message?: string };
}): unknown => {
  if (response.data?.error) {
    throw new Error(response.data.message ?? response.data.error);
  }

  return response.data;
};

/**
 * Every HCBB request carries the token explicitly, as the sibling search
 * services do, and any of them can answer 200 with an `error` field rather
 * than a status code — so that is raised here rather than read as a payload.
 *
 * Almost every endpoint here is a POST with a JSON body — `post` below is the
 * shape callers reach for. Recent-searches is the one exception: it is a GET,
 * and the real API rejects the same fields sent as a body ("Required
 * parameters [...] but found []") — a GET body is non-standard and gets
 * dropped somewhere between the browser and the backend, so this endpoint's
 * fields go as query params instead, same as `httpClient.get`'s convention.
 */
const request = async (
  method: "GET" | "POST",
  url: string,
  data: HcbbRequestBody,
  userInfo: IUserInfo,
  signal?: AbortSignal,
): Promise<unknown> => {
  const response = await httpClient.request({
    method,
    url,
    headers: {
      Authorization: `Bearer ${userInfo.accessToken}`,
      "Content-Type": "application/json",
    },
    ...(method === "GET" ? { params: data } : { data }),
    signal,
  });

  return raiseOnErrorBody(response);
};

const post = (
  url: string,
  data: HcbbRequestBody,
  userInfo: IUserInfo,
  signal?: AbortSignal,
): Promise<unknown> => request("POST", url, data, userInfo, signal);

/**
 * Reads the four result groups off the response body. A group the endpoint
 * leaves out reads as empty rather than undefined, so callers never have to
 * guard each one.
 */
const toSearchResponse = (body: unknown): HcbbFreeTextSearchResponse => {
  const groups = toPayload(body);

  return {
    procedures: toArray<HcbbSearchResultRow>(groups.procedures),
    doctors: toArray<HcbbSearchResultRow>(groups.doctors),
    facilities: toArray<HcbbSearchResultRow>(groups.facilities),
    specialties: toArray<HcbbSearchResultRow>(groups.specialties),
  };
};

/**
 * Free-text search behind the enhanced cost estimator's dropdown. The term is
 * matched server-side, so what comes back is already the result list — the
 * groups are returned verbatim and shaped for display in the hook layer.
 */
export const hcbbSearch = async (
  term: string,
  userInfo: HcbbMemberInfo,
): Promise<HcbbFreeTextSearchResponse> => {
  const data: HcbbSearchRequest = {
    term,
    zipCode: userInfo.zipCode,
    ...toMemberPayload(userInfo),
  };

  const response = await post(API_ENDPOINTS.HCBB.SEARCH, data, userInfo);

  return toSearchResponse(response);
};

/**
 * The names the estimate has been seen to give a professional's id.
 *
 * The details endpoint takes `professionalId`, but the estimate's own list
 * answers with `providerId`. The two have to be reconciled here: reading only
 * the request's name drops every reference, and the providers tab then reads
 * as empty for a search that found plenty.
 *
 * TODO: narrow to the one name the estimate really uses once the payload is
 * settled, and drop the rest.
 */
const PROFESSIONAL_ID_KEYS = ["professionalId", "providerId", "id"];

/**
 * One professional row off the estimate, or null when it cannot be sent to
 * the details endpoint later.
 *
 * A row missing either half of its identity is unusable there — batching is
 * positional, so one bad entry would take the other nine in its request down
 * with it — so it is dropped here rather than kept. Every other field is read
 * defensively (a default per field, `toFacilityProvider`'s style) rather than
 * cast wholesale: the estimate has been confirmed to send full rows in
 * production, but individual fields have still been seen absent when HCBB
 * has partial data for a provider.
 */
const toEstimateProfessional = (
  value: unknown,
): HcbbProfessionalResult | null => {
  const row = (value ?? {}) as Record<string, unknown>;
  const professionalId = PROFESSIONAL_ID_KEYS.reduce(
    (found, key) => found || String(row[key] ?? "").trim(),
    "",
  );
  const npi = Number(row.npi);

  if (!professionalId || !Number.isFinite(npi)) return null;

  return {
    id: Number(row.id) || 0,
    professionalId,
    npi,
    displayName: String(row.displayName ?? ""),
    facilityName: String(row.facilityName ?? ""),
    phone: String(row.phone ?? ""),
    specialty: String(row.specialty ?? ""),
    networkName: String(row.networkName ?? ""),
    displayRadius: String(row.displayRadius ?? ""),
    estimatedCost: Number(row.estimatedCost) || 0,
    actualCost: Number(row.actualCost) || 0,
    address: String(row.address ?? ""),
    longitude: (row.longitude as number | string) ?? "NA",
    latitude: (row.latitude as number | string) ?? "NA",
    offices: toArray(row.offices),
    institution:
      (row.institution as HcbbProfessionalResult["institution"]) ?? null,
    languages: toArray<string>(row.languages),
    photoUrl: String(row.photoUrl ?? ""),
    tier: String(row.tier ?? ""),
    facilityType: String(row.facilityType ?? ""),
    acceptingNewPatients: Boolean(row.acceptingNewPatients),
  };
};

/**
 * The estimate's professionals, in full.
 *
 * Repeats are collapsed as well as dropped: a duplicate would cost one of the
 * ten slots in its batch and then render the same provider twice.
 */
const toEstimateProfessionals = (value: unknown): HcbbProfessionalResult[] => {
  const seen = new Set<string>();

  return toArray<unknown>(value).reduce<HcbbProfessionalResult[]>(
    (rows, value) => {
      const row = toEstimateProfessional(value);
      if (!row || seen.has(row.professionalId)) return rows;

      seen.add(row.professionalId);
      return [...rows, row];
    },
    [],
  );
};

/**
 * Reads the priced benchmark and the two provider lists off the response.
 *
 * A partial answer is normal — HCBB prices procedures it has no in-network
 * provider for, and lists providers it has no benchmark for — so each half is
 * normalised on its own and an absent one lands as empty rather than missing.
 */
const toEstimateResponse = (body: unknown): HcbbProcedureDetailsResponse => {
  const payload = toPayload(body);
  const rows = (payload.data ?? {}) as Record<string, unknown>;

  return {
    data: {
      facilities: toArray<HcbbFacilityResult>(rows.facilities),
      professionals: toEstimateProfessionals(rows.professionals),
    },
    highlight: (payload.highlight as HcbbPriceHighlight | null) ?? null,
  };
};

/**
 * Prices one picked procedure against the providers around a location.
 *
 * The endpoint pages nothing: one call answers with the whole result set, which
 * is why the results list pages client-side.
 */
export const hcbbEstimate = async (
  { procedure, targetLocation, distance }: HcbbCostSearch,
  userInfo: HcbbMemberInfo,
): Promise<HcbbProcedureDetailsResponse> => {
  if (!procedure) {
    throw new Error("A procedure is required to estimate costs.");
  }

  const data: HcbbEstimateRequest = {
    procedureId: toHcbbProcedureId(procedure.code),
    displayName: procedure.description,
    zipCode: toZipCode(targetLocation) || userInfo?.zipCode || "",
    address: toAddress(targetLocation),
    distance: distance?.value ?? null,
    ...toMemberPayload(userInfo),
  };

  const response = await post(API_ENDPOINTS.HCBB.ESTIMATE, data, userInfo);

  return toEstimateResponse(response);
};

/**
 * The professional rows off the details response.
 *
 * The envelope is unconfirmed: the sibling endpoints variously answer with a
 * `results` wrapper, a `data` wrapper and a bare group, so all of those are
 * read and anything else lands as empty rather than throwing.
 *
 * TODO: narrow this to the one shape the endpoint actually uses once a real
 * response has been seen.
 */
const toProfessionalsResponse = (body: unknown): HcbbProfessionalResult[] => {
  const payload = toPayload(body) as unknown;
  if (Array.isArray(payload)) return payload as HcbbProfessionalResult[];

  const envelope = (payload ?? {}) as Record<string, unknown>;
  const rows = envelope.data as unknown;
  if (Array.isArray(rows)) return rows as HcbbProfessionalResult[];

  const group = (rows ?? {}) as Record<string, unknown>;

  return toArray<HcbbProfessionalResult>(
    group.professionals ?? envelope.professionals,
  );
};

/**
 * Resolves each detail-endpoint row's canonical `professionalId`, and fills
 * in `npi` when the row itself didn't carry a usable one.
 *
 * This endpoint's response shape has never been confirmed to echo either
 * field back under those exact names per row (unlike the estimate, whose
 * payload has been observed directly) — the same `professionalId`/`providerId`
 * ambiguity the estimate itself has, and possibly no id field at all. Left
 * unresolved, a row like that gets cached under `professionalId: undefined`
 * by the hook's per-professional cache, which never matches the candidate's
 * own key — the batch fetch succeeds, but the providers tab renders nothing
 * from it. So both fields are resolved here against the batch that was
 * requested, the source of truth for the identities actually asked for.
 * Matched by the row's own resolved id first, since that survives the
 * response reordering rows; falls back to matching by position only when the
 * two lists are the same length, since a length mismatch means at least one
 * row cannot be attributed with confidence.
 */
const withKnownIdentity = (
  rows: HcbbProfessionalResult[],
  batch: HcbbProfessionalRef[],
): HcbbProfessionalResult[] => {
  const refById = new Map(batch.map((ref) => [ref.professionalId, ref]));

  return rows.map((row, index) => {
    const resolvedId = PROFESSIONAL_ID_KEYS.reduce(
      (found, key) =>
        found || String((row as Record<string, unknown>)[key] ?? "").trim(),
      "",
    );
    const matchedRef =
      refById.get(resolvedId) ??
      (rows.length === batch.length ? batch[index] : undefined);

    // The matched reference's own `professionalId` wins over the row's raw
    // resolved id: when the row's id didn't match anything in the batch, the
    // resolved id is whatever unrelated field PROFESSIONAL_ID_KEYS happened to
    // find (often a bare numeric `id`), not a usable identity — the reference
    // this row was positionally attributed to is the trustworthy one.
    const professionalId = matchedRef?.professionalId ?? resolvedId;
    const npi = Number.isFinite(row.npi) ? row.npi : matchedRef?.npi;

    if (professionalId === row.professionalId && npi === row.npi) return row;

    return { ...row, professionalId, npi: npi ?? row.npi };
  });
};

/**
 * Details for one batch of professional references.
 *
 * The estimate answers with references rather than rows because this endpoint
 * degrades badly past a batch of ten, which is why the results list asks for
 * them a batch at a time instead of all at once. `highlight` is that same
 * estimate's own priced benchmark — `majorGroup` and the fee breakdown both
 * describe the procedure being priced, not the batch, so they ride along on
 * every batch of it unchanged. `procedureId` mirrors `/estimate`'s own field —
 * this batch is priced for the same procedure the estimate was.
 */
export const hcbbProfessionals = async (
  {
    procedure,
    targetLocation,
    professionals,
    highlight,
  }: Pick<HcbbCostSearch, "procedure" | "targetLocation"> & {
    professionals: HcbbProfessionalRef[];
    highlight: HcbbPriceHighlight | null;
  },
  userInfo: HcbbMemberInfo,
  signal?: AbortSignal,
): Promise<HcbbProfessionalResult[]> => {
  // An empty batch has no answer worth a round trip, procedure or not.
  if (professionals.length === 0) return [];

  if (!procedure) {
    throw new Error("A procedure is required to price professionals.");
  }

  const data: HcbbProfessionalsRequest = {
    procedureId: toHcbbProcedureId(procedure.code),
    zipCode: toZipCode(targetLocation) || userInfo?.zipCode || "",
    professionals,
    majorGroup: highlight?.majorGroup ?? "",
    additionalFees: toAdditionalFees(highlight?.pricingSections ?? []),
    ...toMemberPayload(userInfo),
  };

  const response = await post(
    API_ENDPOINTS.HCBB.PROFESSIONALS,
    data,
    userInfo,
    signal,
  );

  return withKnownIdentity(toProfessionalsResponse(response), professionals);
};

/**
 * One doctor working at the facility, off the facility-providers response.
 *
 * The envelope is the one documented shape (`{ count, results }`), unlike the
 * professional-details endpoint's — so this narrows to it directly rather
 * than tolerating shapes never seen. A field the endpoint omits still gets a
 * defined fallback, since a doctor missing one fact is still worth showing.
 */
const toFacilityProvider = (value: unknown): HcbbFacilityProvider => {
  const row = (value ?? {}) as Record<string, unknown>;

  return {
    displayName: String(row.displayName ?? ""),
    specialty: String(row.specialty ?? ""),
    networkName: String(row.networkName ?? ""),
    tier: row.tier == null ? null : String(row.tier),
    distance: String(row.distance ?? ""),
    estimatedCost: Number(row.estimatedCost) || 0,
  };
};

/**
 * The facility's doctor list.
 *
 * `count` is read off the parsed list rather than the wire's own count field:
 * the two should agree, but a row this parser cannot make sense of is still
 * one row fewer to claim as shown.
 */
const toFacilityProvidersResponse = (
  body: unknown,
): HcbbFacilityProvidersResponse => {
  const envelope = (body ?? {}) as Record<string, unknown>;
  const results = toArray<unknown>(envelope.results).map(toFacilityProvider);

  return { count: results.length, results };
};

/**
 * The doctors working at one facility, priced for the same procedure and
 * location that found it.
 *
 * Mirrors `/estimate`'s own member, procedure and zip fields — HCBB prices
 * this the same way — with the facility's NPIs and the same estimate's
 * `highlight.majorGroup` as the fields it adds. Takes only two of
 * `HcbbCostSearch`'s three fields: unlike the estimate, this request never
 * carries a `distance`.
 */
export const hcbbFacilityProviders = async (
  {
    procedure,
    targetLocation,
    highlight,
  }: Pick<HcbbCostSearch, "procedure" | "targetLocation"> & {
    highlight: HcbbPriceHighlight | null;
  },
  npis: HcbbFacilityResult["npis"],
  userInfo: HcbbMemberInfo,
  signal?: AbortSignal,
): Promise<HcbbFacilityProvidersResponse> => {
  if (!procedure) {
    throw new Error("A procedure is required to price a facility's providers.");
  }

  const npisList = toNpisArray(npis);
  // No NPI on file is nothing to price, and not worth a round trip for —
  // the same shortcut `hcbbProfessionals` takes for an empty batch.
  if (npisList.length === 0) return { count: 0, results: [] };

  const data: HcbbFacilityProvidersRequest = {
    procedureId: toHcbbProcedureId(procedure.code),
    zipCode: toZipCode(targetLocation) || userInfo?.zipCode || "",
    npis: npisList,
    majorGroup: highlight?.majorGroup ?? "",
    ...toMemberPayload(userInfo),
  };

  const response = await post(
    API_ENDPOINTS.HCBB.PROVIDER_ESTIMATES,
    data,
    userInfo,
    signal,
  );

  return toFacilityProvidersResponse(response);
};

/**
 * One history row, or null when it could not be run again.
 *
 * A row is a control here, not a label: without a procedure id and a zip to
 * price it against, clicking it would land the member on a results page that
 * can only fail. Those are dropped rather than rendered.
 */
const toRecentSearch = (value: unknown): HcbbRecentSearch | null => {
  const row = (value ?? {}) as Record<string, unknown>;
  const procedureId = String(row.procedureId ?? "").trim();
  const zipCode = String(row.zipCode ?? "").trim();

  if (!procedureId || !zipCode) return null;

  const distance = Number(row.distance) || 0;

  return {
    // The endpoint sends no row id, and the same procedure/zip can recur
    // with a different radius, so all three go into the React list key.
    id: `${procedureId}-${zipCode}-${distance}`,
    procedureId,
    // Falls back to the bare id/zip rather than dropping the row — a row
    // is still runnable without a friendly label, just less readable.
    displayName: String(row.displayName ?? "").trim() || procedureId,
    zipCode,
    address: String(row.address ?? "").trim() || zipCode,
    distance,
  };
};

/** The history rows off the response body, unusable ones dropped. */
const toRecentSearches = (body: unknown): HcbbRecentSearch[] =>
  toArray<unknown>(toPayload(body)).reduce<HcbbRecentSearch[]>(
    (searches, row) => {
      const search = toRecentSearch(row);
      return search ? [...searches, search] : searches;
    },
    [],
  );

/**
 * Rows requested per page — enough to fill the panel's fixed 3-row list
 * without over-fetching a history the member may never scroll. Exported so
 * the hook can cap the same list at the same length when it prepends a
 * just-run search locally, without asking the server again.
 */
export const RECENT_SEARCHES_SIZE = 3;

/**
 * The recent-searches endpoint takes its own member subset, not
 * `toMemberPayload`'s: a username in place of an employee id or coverage
 * period, plus a page size.
 */
const toRecentSearchesPayload = (
  userInfo: HcbbMemberInfo,
): HcbbRecentSearchesRequest => ({
  username: userInfo.username,
  size: RECENT_SEARCHES_SIZE,
  groupId: userInfo.groupId,
  divisionId: userInfo.divisionId,
  planId: userInfo.planId,
  memberId: userInfo.memberId,
});

/** The searches this member has run before, newest first. */
export const hcbbRecentSearches = async (
  userInfo: HcbbMemberInfo,
): Promise<HcbbRecentSearch[]> =>
  toRecentSearches(
    await request(
      "GET",
      API_ENDPOINTS.HCBB.RECENT_SEARCH,
      toRecentSearchesPayload(userInfo),
      userInfo,
    ),
  );
